import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { subscribeToVisibleJobs, type Job } from '@buildmate/core';
import { Badge, Button, Field, cn } from '@buildmate/ui-web';
import type { StatusTone } from '@buildmate/tokens';
import { usePorts } from '../lib/ports-context.tsx';
import { useSession } from '../lib/session-context.tsx';

const STATUS_TONE: Record<Job['status'], StatusTone> = {
  lead: 'info',
  open: 'success',
  closed: 'neutral',
  archived: 'neutral',
};

/**
 * The navigation spine. Selecting a job here sets the context every module
 * screen reads from the URL, which is why the job id lives in the path rather
 * than in component state: a link to a schedule is a link to *that job's*
 * schedule, and the back button behaves.
 */
export function JobSidebar({ onNewJob, canCreate }: { onNewJob: () => void; canCreate: boolean }) {
  const ports = usePorts();
  const { membership } = useSession();
  const params = useParams({ strict: false }) as { jobId?: string };
  const [jobs, setJobs] = useState<readonly Job[] | null>(null);
  const [search, setSearch] = useState('');
  const [showClosed, setShowClosed] = useState(false);

  useEffect(() => {
    /*
      Only the jobs this person may open (SCRUM-294). The `jobs` list
      rule leaves the filtering to the client, and nothing did it — so
      a member assigned to two jobs saw the name and address of every
      job in the company in this sidebar.
    */
    return subscribeToVisibleJobs(
      { data: ports.data, companyId: membership.companyId },
      membership,
      setJobs,
      () => setJobs([]),
      { orderBy: [{ field: 'name', direction: 'asc' }] },
    );
  }, [ports, membership]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (jobs ?? []).filter((job) => {
      if (!showClosed && (job.status === 'closed' || job.status === 'archived')) return false;
      if (!term) return true;
      return (
        job.name.toLowerCase().includes(term) ||
        job.prefix.toLowerCase().includes(term) ||
        job.address.city.toLowerCase().includes(term)
      );
    });
  }, [jobs, search, showClosed]);

  const openCount = (jobs ?? []).filter((j) => j.status === 'open').length;

  return (
    // Hidden below `md`. At 390px this was 288px wide and left the content
    // 102px, which is not a narrow layout — it is an unusable one. A phone
    // reaches jobs through the Jobs link in the header instead, which is
    // the same list on its own page.
    <aside className="hidden w-72 shrink-0 flex-col border-r border-slate-200 bg-white md:flex">
      <div className="flex flex-col gap-3 border-b border-slate-200 p-4">
        {canCreate && (
          <Button className="w-full" onClick={onNewJob}>
            New job
          </Button>
        )}
        <Field
          label="Search jobs"
          placeholder="Name, prefix or city"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={showClosed}
            onChange={(e) => setShowClosed(e.target.checked)}
            className="rounded border-slate-300"
          />
          Show closed and archived
        </label>
      </div>

      <div className="px-4 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">
        {jobs === null ? 'Loading…' : `${openCount} open ${openCount === 1 ? 'job' : 'jobs'}`}
      </div>

      <nav aria-label="Jobs" className="min-h-0 flex-1 overflow-y-auto pb-4">
        {jobs !== null && visible.length === 0 && (
          <p className="px-4 py-3 text-sm text-slate-500">
            {jobs.length === 0 ? 'No jobs yet.' : 'No jobs match that search.'}
          </p>
        )}

        <ul>
          {visible.map((job) => {
            const selected = params.jobId === job.id;
            return (
              <li key={job.id}>
                <Link
                  to="/app/jobs/$jobId"
                  params={{ jobId: job.id }}
                  aria-current={selected ? 'page' : undefined}
                  className={cn(
                    'block border-l-2 px-4 py-3 text-sm transition-colors',
                    selected
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-transparent hover:bg-slate-50',
                  )}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="truncate font-medium text-slate-900">{job.name}</span>
                    <Badge tone={STATUS_TONE[job.status]}>{job.status}</Badge>
                  </span>
                  {/*
                    slate-600, not slate-500: on the selected row's blue-50
                    ground slate-500 is 4.37:1 against a required 4.5, and
                    the row a reader has chosen is the one they are most
                    likely to be reading.
                  */}
                  <span className="mt-0.5 block truncate text-xs text-slate-600">
                    {job.prefix} · {job.address.city}, {job.address.state}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}
